import { useState, useEffect, useCallback } from 'react';
import { StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useColors, ThemeColors } from '../hooks/useColors';
import { useRoute, useFocusEffect } from '@react-navigation/native';
import { useChat } from '../hooks/useChat';
import { useChatReadStatus } from '../hooks/useChatReadStatus';
import { ChatHeader } from '../components/chat/ChatHeader';
import { ChatMessages } from '../components/chat/ChatMessages';
import { ChatInput } from '../components/chat/ChatInput';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import type { RootStackScreenProps } from '../types/navigation';
import { useQueryClient } from '@tanstack/react-query';

export function ChatScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const insets = useSafeAreaInsets();
  const route = useRoute<RootStackScreenProps<'Chat'>['route']>();
  const chatId = route.params?.id;
  const [isTemp, setIsTemp] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const { markChatAsRead } = useChatReadStatus();

  // Check if this is a temporary chat that's being created
  useEffect(() => {
    if (chatId && chatId.startsWith('temp-')) {
      setIsTemp(true);
    } else {
      setIsTemp(false);
    }
  }, [chatId]);

  // Mark chat as read when screen is focused (not just on mount)
  useFocusEffect(
    useCallback(() => {
      if (chatId && !isTemp && !chatId.startsWith('temp-')) {
        console.log('📖 [CHAT_SCREEN] Screen focused, marking chat as read:', chatId);
        markChatAsRead(chatId);
      }
    }, [chatId, isTemp, markChatAsRead])
  );

  if (!chatId) {
    return <ErrorState error={new Error('No chat ID provided')} />;
  }

  // For temporary chats, show loading state
  if (isTemp) {
    return <LoadingState />;
  }

  const { chat, messages, isLoading, error, sendMessage } = useChat(chatId);

  // Handle refresh
  const handleRefresh = async () => {
    if (!chatId) return;
    
    setIsRefreshing(true);
    try {
      // Invalidate both chat and messages queries to force refresh
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chat', chatId] }),
        queryClient.invalidateQueries({ queryKey: ['chat-messages', chatId] })
      ]);
      // Mark as read after refresh
      markChatAsRead(chatId);
    } catch (error) {
      console.error('Error refreshing chat:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    console.error('Chat error:', error);
    return <ErrorState error={error} />;
  }

  if (!chat) {
    return <ErrorState error={new Error('Could not load chat')} />;
  }

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top + 60 : 0}
    >
      <ChatHeader chat={chat} />
      <ChatMessages 
        messages={messages || []} 
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
      <ChatInput onSend={sendMessage} />
    </KeyboardAvoidingView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
}); 